import axios from "axios";

export const NCRM_PBX_ENDPOINT = "/ncrm/integrations/pbx";

const toBoolean = (value) => value === true || value === 1 || value === "1";

const trimOrNull = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed || null;
};

export const createPbxDraft = () => ({
  id: null,
  name: "Orange PBX",
  description: "",
  apiMode: "direct",
  status: "draft",
  baseUrl: "https://infinitech.pbx.orange.md:7443",
  pbxDomain: "infinitech.pbx.orange.md",
  serialNumber: "",
  username: "",
  password: "",
  syncEnabled: true,
  syncIntervalMinutes: 5,
});

export const createPbxDraftFromAccount = (account) => account ? {
  id: account.id,
  name: account.name || "",
  description: account.description || "",
  apiMode: account.api_mode || "direct",
  status: account.status || "draft",
  baseUrl: account.base_url || "",
  pbxDomain: account.pbx_domain || "",
  serialNumber: account.serial_number || "",
  username: account.username || "",
  password: "",
  syncEnabled: toBoolean(account.sync_enabled),
  syncIntervalMinutes: Number(account.sync_interval_minutes || 5),
} : createPbxDraft();

const toDateTimeModel = (date) => {
  const pad = (number) => String(number).padStart(2, "0");
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-") + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const createPbxSyncDraft = (now = new Date()) => ({
  mode: "last24h",
  customStartTime: toDateTimeModel(new Date(now.getTime() - 24 * 60 * 60 * 1000)),
  customEndTime: toDateTimeModel(now),
});

export const normalizePbxAccount = (account = {}) => ({
  ...account,
  id: Number(account.id),
  sync_enabled: toBoolean(account.sync_enabled),
  sync_interval_minutes: Number(account.sync_interval_minutes || 5),
});

export const normalizePbxCall = (call = {}) => ({
  ...call,
  id: Number(call.id),
  account_id: Number(call.account_id),
  duration_seconds: Number(call.duration_seconds || 0),
  talk_duration_seconds: Number(call.talk_duration_seconds || 0),
  ring_duration_seconds: Number(call.ring_duration_seconds || 0),
  activity_id: call.activity_id ? Number(call.activity_id) : null,
});

export const normalizePbxSyncResult = (result = {}) => {
  const imported = Number(result.imported || 0);
  const matched = Number(result.matched || 0);
  return {
    ok: result.ok !== false,
    imported,
    matched,
    unmatched: Math.max(0, imported - matched),
    activities: Number(result.activities || 0),
    skipped: Number(result.skipped || 0),
    last_cdr_synced_at: result.last_cdr_synced_at || null,
  };
};

export const buildPbxAccountPayload = (draft, { includePassword = false } = {}) => {
  const payload = {
    name: String(draft.name || "").trim(),
    description: trimOrNull(draft.description),
    api_mode: draft.apiMode === "proxy" ? "proxy" : "direct",
    status: draft.status || "draft",
    base_url: String(draft.baseUrl || "").trim(),
    pbx_domain: String(draft.pbxDomain || "").trim(),
    serial_number: trimOrNull(draft.serialNumber),
    username: String(draft.username || "").trim(),
    sync_enabled: Boolean(draft.syncEnabled),
    sync_interval_minutes: Number(draft.syncIntervalMinutes || 5),
  };
  if (includePassword) {
    payload.password = String(draft.password || "").trim();
  }
  return payload;
};

export const formatPbxDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (number) => String(number).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const formatPbxDuration = (seconds) => {
  const total = Math.max(0, Number(seconds || 0));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
};

const toApiDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const buildPbxSyncWindow = (mode = "last24h", { now = new Date(), customStartTime = "", customEndTime = "" } = {}) => {
  if (mode === "incremental") return {};
  if (mode === "custom") {
    return {
      startTime: toApiDateTime(customStartTime),
      endTime: toApiDateTime(customEndTime),
    };
  }
  const hours = mode === "last7d" ? 24 * 7 : 24;
  return {
    startTime: new Date(now.getTime() - hours * 60 * 60 * 1000).toISOString(),
    endTime: now.toISOString(),
  };
};

export const resolvePbxStatusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active" || normalized === "ok" || normalized === "answered" || normalized === "matched") {
    return { color: "success", variant: "tonal" };
  }
  if (normalized === "draft" || normalized === "unknown" || normalized === "unmatched" || normalized === "never") {
    return { color: "neutral", variant: "tonal" };
  }
  if (normalized === "paused" || normalized === "no answer" || normalized === "busy") {
    return { color: "warning", variant: "tonal" };
  }
  if (normalized === "error" || normalized === "failed" || normalized === "disabled") {
    return { color: "error", variant: "tonal" };
  }
  if (normalized === "linked_manual" || normalized === "activity_created") {
    return { color: "info", variant: "tonal" };
  }
  return { color: "neutral", variant: "tonal" };
};

export const fetchPbxAccounts = () => axios.get(`${NCRM_PBX_ENDPOINT}/accounts`);

export const createPbxAccount = (payload) => axios.post(`${NCRM_PBX_ENDPOINT}/accounts`, payload);

export const updatePbxAccount = (id, payload) => axios.patch(`${NCRM_PBX_ENDPOINT}/accounts/${id}`, payload);

export const testPbxAccount = (id) =>
  axios.post(`${NCRM_PBX_ENDPOINT}/accounts/${id}/test`, {}, { validateStatus: () => true });

export const syncPbxAccount = (id, payload = {}) => axios.post(`${NCRM_PBX_ENDPOINT}/accounts/${id}/sync`, payload);

export const fetchPbxCalls = (params = {}) => axios.get(`${NCRM_PBX_ENDPOINT}/calls`, { params });

export const linkPbxCall = (id, payload) => axios.post(`${NCRM_PBX_ENDPOINT}/calls/${id}/link`, payload);

export const fetchPbxRecording = (callId) =>
  axios.get(`${NCRM_PBX_ENDPOINT}/calls/${callId}/recording`, { responseType: "blob" });

export const parseContentDispositionFilename = (header, fallback = "recording.wav") => {
  const raw = String(header || "");
  const star = raw.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
    } catch {
      /* fall through */
    }
  }
  const plain = raw.match(/filename="?([^";]+)"?/i);
  return plain && plain[1] ? plain[1].trim() : fallback;
};

export const saveBlobToDisk = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || "recording.wav";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

// Descarcă înregistrarea unui apel prin backend (streaming, fără expunere de credențiale).
// Rezolvă la true la succes; aruncă eroarea pentru ca apelantul să afișeze prompt-ul potrivit
// (410 = înregistrare indisponibilă/expirată).
export const triggerPbxRecordingDownload = async (callId) => {
  const response = await fetchPbxRecording(callId);
  const filename = parseContentDispositionFilename(
    response.headers?.["content-disposition"],
    `recording-${callId}.wav`
  );
  saveBlobToDisk(response.data, filename);
  return true;
};

export const isPbxRecordingUnavailableError = (error) => Number(error?.response?.status) === 410;
